import {
  ChatMessagePush,
  MessageDto,
  OneOnOneChatDay,
  OneOnOneChatDelivery,
  OneOnOneChatMessage,
  OneOnOneChatSenderType,
} from '../model/one-on-one-chat.types';

/* Only the candidate's own messages go right; consultant (and unknown) messages go left. */
export const mapMessageDto = (dto: MessageDto): OneOnOneChatMessage => ({
  id: dto.messageId,
  isOwn: dto.senderType === OneOnOneChatSenderType.Candidate,
  text: dto.message,
  createdOn: new Date(dto.createdOn),
  delivery: OneOnOneChatDelivery.Sent,
});

export const mapMessagePush = (push: ChatMessagePush): OneOnOneChatMessage => ({
  id: push.messageId,
  isOwn: false,
  text: push.message,
  createdOn: new Date(push.createdOn),
  delivery: OneOnOneChatDelivery.Sent,
});

export const sortOldestFirst = (messages: OneOnOneChatMessage[]): OneOnOneChatMessage[] =>
  [...messages].sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime());

/* Pages can overlap when messages arrive between two requests, so drop what we already have. */
export const mergeMessages = (
  current: OneOnOneChatMessage[],
  incoming: OneOnOneChatMessage[],
): OneOnOneChatMessage[] => {
  const known = new Set(current.map((message) => message.id));
  return sortOldestFirst([...current, ...incoming.filter((message) => !known.has(message.id))]);
};

export const findFirstUnread = (messages: OneOnOneChatMessage[], lastReadMessageId: string | null): string | null => {
  const lastReadIndex = lastReadMessageId ? messages.findIndex((message) => message.id === lastReadMessageId) : -1;
  return messages.slice(lastReadIndex + 1).find((message) => !message.isOwn)?.id ?? null;
};

/* Expects messages sorted oldest first; groups them per local calendar day. */
export const groupByDay = (messages: OneOnOneChatMessage[]): OneOnOneChatDay[] =>
  messages.reduce<OneOnOneChatDay[]>((days, message) => {
    const last = days.at(-1);
    if (last && last.day.toDateString() === message.createdOn.toDateString()) {
      last.messages.push(message);
    } else {
      days.push({ day: message.createdOn, messages: [message] });
    }
    return days;
  }, []);
